import type { HTMLAttributes, LabelHTMLAttributes, ReactNode } from "react";

function joinClassNames(...values: Array<string | false | null | undefined>) {
  return values.filter(Boolean).join(" ");
}

export function SectionHeader({
  title,
  description,
  action,
  className
}: {
  title: ReactNode;
  description?: ReactNode;
  action?: ReactNode;
  className?: string;
}) {
  return (
    <div className={joinClassNames("section-header ui-section-header", className)}>
      <div className="ui-section-heading">
        <h2>{title}</h2>
        {description ? <p>{description}</p> : null}
      </div>
      {action}
    </div>
  );
}

export function Card({ className, ...props }: HTMLAttributes<HTMLDivElement>) {
  return <div className={joinClassNames("card ui-card", className)} {...props} />;
}

export function CardHeader({ className, ...props }: HTMLAttributes<HTMLDivElement>) {
  return <div className={joinClassNames("ui-card-header", className)} {...props} />;
}

export function CardTitle({ className, ...props }: HTMLAttributes<HTMLHeadingElement>) {
  return <h3 className={joinClassNames("ui-card-title", className)} {...props} />;
}

export function CardDescription({ className, ...props }: HTMLAttributes<HTMLParagraphElement>) {
  return <p className={joinClassNames("ui-card-description", className)} {...props} />;
}

export function CardContent({ className, ...props }: HTMLAttributes<HTMLDivElement>) {
  return <div className={joinClassNames("ui-card-content", className)} {...props} />;
}

export function CardFooter({ className, ...props }: HTMLAttributes<HTMLDivElement>) {
  return <div className={joinClassNames("ui-card-footer", className)} {...props} />;
}

export function EmptyState({
  icon,
  title,
  children,
  action,
  className
}: {
  icon?: ReactNode;
  title?: ReactNode;
  children?: ReactNode;
  action?: ReactNode;
  className?: string;
}) {
  return (
    <section className={joinClassNames("empty-state ui-empty-state", className)}>
      {icon ? <div className="ui-empty-icon" aria-hidden="true">{icon}</div> : null}
      {title ? <h1>{title}</h1> : null}
      {children ? <p>{children}</p> : null}
      {action ? <div className="ui-empty-action">{action}</div> : null}
    </section>
  );
}

export function Field({
  label,
  children,
  hint,
  className,
  ...props
}: HTMLAttributes<HTMLDivElement> & {
  label: ReactNode;
  hint?: ReactNode;
}) {
  return (
    <div className={joinClassNames("form-group ui-field", className)} {...props}>
      <label>
        <span>{label}</span>
        {hint ? <span className="ui-field-hint">{hint}</span> : null}
      </label>
      {children}
    </div>
  );
}

export function Label({ className, ...props }: LabelHTMLAttributes<HTMLLabelElement>) {
  return <label className={joinClassNames("ui-label", className)} {...props} />;
}

export function Separator({ className, ...props }: HTMLAttributes<HTMLDivElement>) {
  return <div className={joinClassNames("divider ui-separator", className)} role="separator" {...props} />;
}

export function ActionRow({ className, ...props }: HTMLAttributes<HTMLDivElement>) {
  return <div className={joinClassNames("ui-action-row", className)} {...props} />;
}
